import React from "react";
import { usePageMeta } from "../../lib/seo";
import { Shield, Mail, Phone, FileText } from "lucide-react";

const PrivacyPolicy = () => {
  usePageMeta({ title: "Privacy Policy | NeatWave Cleaning Services", description: "How NeatWave Cleaning Services collects, uses and protects your personal information." });
  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-8 h-8 text-sky-600" />
            <h1 className="text-3xl font-bold text-slate-900">Privacy Policy</h1>
          </div>
          <p className="text-slate-600">
            Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Introduction</h2>
            <p className="text-slate-600 leading-relaxed">
              NeatWave Cleaning ("we", "our", or "us") is committed to protecting your personal information and your right to privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website 
              or use our cleaning services in Cardiff, Barry and Penarth.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Information We Collect</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Personal Information</h3>
                <p className="text-slate-600 leading-relaxed mb-2">
                  When you request a quote or book our services, we collect:
                </p>
                <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
                  <li>Full name</li>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>Property address</li>
                  <li>Property type and details (bedrooms, bathrooms)</li>
                  <li>Service preferences and requirements</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Automatically Collected Information</h3>
                <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
                  <li>Browser type and version</li>
                  <li>IP address (anonymized)</li>
                  <li>Pages visited and time spent</li>
                  <li>Essential cookies for website functionality</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Information */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-slate-600 leading-relaxed mb-3">
              We use your personal information to:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Provide and manage our cleaning services</li>
              <li>Send you service quotes and confirmations</li>
              <li>Communicate about appointments and scheduling</li>
              <li>Respond to your inquiries and customer service requests</li>
              <li>Send important updates about your bookings</li>
              <li>Improve our website and services</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          {/* Legal Basis (GDPR) */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Legal Basis for Processing (GDPR)</h2>
            <p className="text-slate-600 leading-relaxed mb-3">
              Under UK GDPR, we process your personal data based on:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li><strong>Contract:</strong> To perform our cleaning services you've requested</li>
              <li><strong>Consent:</strong> When you provide information through our quote form</li>
              <li><strong>Legitimate Interest:</strong> To improve our services and respond to inquiries</li>
              <li><strong>Legal Obligation:</strong> To maintain records required by UK law</li>
            </ul>
          </section>

          {/* Data Sharing */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Information Sharing and Disclosure</h2>
            <p className="text-slate-600 leading-relaxed mb-3">
              We do NOT sell or rent your personal information. We may share information only with:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li><strong>Our cleaning team:</strong> To provide services at your property</li>
              <li><strong>Payment processors:</strong> To process payments securely (if applicable)</li>
              <li><strong>Legal authorities:</strong> When required by law or to protect our rights</li>
            </ul>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Cookies</h2>
            <p className="text-slate-600 leading-relaxed">
              We use essential cookies to ensure our website functions properly and to remember your preferences.
              If you choose "Accept" on our cookie notice, we also use Google Analytics 4 cookies to measure visits and
              enquiries (for example clicks on our phone and WhatsApp links). Analytics is off until you accept, IP addresses
              are anonymised, and we do not use advertising cookies. You can change your choice by clearing this site's data
              in your browser. Enquiry details, including any property photos you upload, are used only to respond to your
              request and to send the service emails described above.
            </p>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Data Security</h2>
            <p className="text-slate-600 leading-relaxed">
              We implement appropriate technical and organizational measures to protect your personal information, including:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4 mt-3">
              <li>Secure HTTPS encryption for data transmission</li>
              <li>Password-protected admin access</li>
              <li>Regular security updates and backups</li>
              <li>Limited access to personal data (staff only on need-to-know basis)</li>
            </ul>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Data Retention</h2>
            <p className="text-slate-600 leading-relaxed">
              We retain your personal information for as long as necessary to provide our services and comply with legal obligations:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4 mt-3">
              <li><strong>Active customers:</strong> Duration of service relationship</li>
              <li><strong>Quote requests:</strong> Up to 12 months</li>
              <li><strong>Financial records:</strong> 6 years (UK tax law requirement)</li>
            </ul>
          </section>

          {/* Your Rights (GDPR) */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Your Rights Under UK GDPR</h2>
            <p className="text-slate-600 leading-relaxed mb-3">
              You have the right to:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li><strong>Access:</strong> Request copies of your personal data</li>
              <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
              <li><strong>Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
              <li><strong>Restriction:</strong> Limit how we use your data</li>
              <li><strong>Portability:</strong> Receive your data in a structured format</li>
              <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent at any time</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              To exercise any of these rights, please contact us using the details below.
            </p>
          </section>

          {/* Third-Party Links */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Third-Party Links</h2>
            <p className="text-slate-600 leading-relaxed">
              Our website may contain links to social media platforms (Facebook, Instagram, WhatsApp). 
              These third-party sites have their own privacy policies, and we are not responsible for their practices.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Children's Privacy</h2>
            <p className="text-slate-600 leading-relaxed">
              Our services are not directed to individuals under 18. We do not knowingly collect personal information 
              from children. If you believe a child has provided us with personal data, please contact us immediately.
            </p>
          </section>

          {/* Changes to Privacy Policy */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Changes to This Privacy Policy</h2>
            <p className="text-slate-600 leading-relaxed">
              We may update this Privacy Policy from time to time. The "Last updated" date at the top will reflect 
              the most recent changes. We encourage you to review this page periodically.
            </p>
          </section>

          {/* Contact Information */}
          <section className="bg-sky-50 rounded-lg p-6 border border-sky-100">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">13. Contact Us</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              If you have questions about this Privacy Policy or wish to exercise your rights, please contact us:
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-slate-700">
                <Mail className="w-5 h-5 text-sky-600" />
                <span><strong>Email:</strong> info@neatwavecleaning.co.uk</span>
              </div>
              <div className="flex items-center gap-3 text-slate-700">
                <Phone className="w-5 h-5 text-sky-600" />
                <span><strong>Phone:</strong> 0749 286 5011</span>
              </div>
              <div className="flex items-center gap-3 text-slate-700">
                <FileText className="w-5 h-5 text-sky-600" />
                <span><strong>Business:</strong> NeatWave Cleaning Services, Cardiff, Wales, UK</span>
              </div>
            </div>
            <p className="text-sm text-slate-600 mt-4">
              You also have the right to lodge a complaint with the Information Commissioner's Office (ICO), 
              the UK's data protection authority, at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline">ico.org.uk</a>
            </p>
          </section>
        </div>

        {/* Back to Home */}
        <div className="mt-8 text-center">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-sky-600 hover:text-sky-700 font-medium"
          >
            ← Back to Home
          </a>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
